import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addCompFinalTreatmentOwnToBusiness,
  getCompFinalTreatmentOwnList,
  updateCompFinalTreatmentOwn,
  deleteCompFinalTreatmentOwn,
} from "../../../../../../apis/Business/CompanyFinalTreatmentOwnApi";
import {
  getCompFinalTreatmentDropdown,
  getTransportCountryData,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const FinalTreatmentOwnTable = ({ businessId, category }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownProcess, setDropdownProcess] = useState([]);
  const [dropdownCountry, setDropdownCountry] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getCompFinalTreatmentOwnList({
      businessId,
      setObjects: setData,
    });
    getCompFinalTreatmentDropdown({ setDropdown: setDropdownProcess });
    getTransportCountryData({ setDropdownData: setDropdownCountry });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownProcess?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["end_of_life_strategy.value"]?.value ||
          row.end_of_life_strategy?.value;

        if (materialValue) {
          const selectedMaterial = dropdownProcess.find(
            (option) =>
              option?.dropdown === materialValue ||
              option?.dropdown_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownProcess]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "end_of_life_strategy.value": {
        targetField: "total_weight_treated",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedItem = dropdownData.find(
            (item) =>
              item.dropdown === selectedValue ||
              item.dropdown_en === selectedValue
          );
          if (selectedItem) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedItem.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedItem.unit,
              }));
            }
          }
        },
        dropdownDataKey: "end_of_life_strategy.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      updateCompFinalTreatmentOwn({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        category: category,
        end_of_life_strategy: editedItem["end_of_life_strategy.value"]
          ? editedItem["end_of_life_strategy.value"].value
          : editedItem["end_of_life_strategy"]
          ? editedItem["end_of_life_strategy"].value
          : null,
        treatment_country: editedItem["treatment_country.value"]
          ? editedItem["treatment_country.value"].value
          : editedItem["treatment_country"]
          ? editedItem["treatment_country"].value
          : null,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData, category]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addCompFinalTreatmentOwnToBusiness({
        businessId,
        ...newItem,
        category: category,
        end_of_life_strategy: newItem["end_of_life_strategy.value"]
          ? newItem["end_of_life_strategy.value"].value
          : null,
        treatment_country: newItem["treatment_country.value"]
          ? newItem["treatment_country.value"].value
          : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, category]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteCompFinalTreatmentOwn({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "120px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "120px",
      },
      {
        accessor: "product_name",
        header: t("PRODUCT_NAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "is_disassembly",
        header: t("IS_DISASSEMBLY"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
        tipHeader: "Tip",
        tipBody: t("FINAL_DISASSEMBLY_TIP"),
      },
      {
        accessor: "product_component",
        header: t("PRODUCT_COMPONENT"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "end_of_life_strategy.value",
        header: `${t("END_OF_LIFE_STRATEGY")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "130px",
        isSubgroup: true,
        // tipHeader: "Tip",
        // tipBody: t("end_of_life_strategy_NO_DATA_TIP"),
      },
      {
        accessor: "is_leased_take_back",
        header: t("LEASED_OR_TAKE_BACK"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
      },
      {
        accessor: "total_weight_treated",
        header: `${t("COMP_FINAL_TREATMENT_QTY")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
      },
      {
        accessor: "treatment_country.value",
        header: `${t("TREATMENT_COUNTRY")}`,
        type: "dropdown",
        dropdownData: dropdownCountry,
        disableSortBy: true,
        width: "130px",
        // tipHeader: "Tip",
        // tipBody: t("COUNTRY_PROCESSING_TIP"),
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownProcess, dropdownCountry, unitMaterials, t]
  );

  const disableRules = {
    carbon: () => true,
    product_component: (rowData) =>
      rowData.is_disassembly === "No" || rowData.is_disassembly === "Nej",
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "end_of_life_strategy.value": dropdownProcess,
      "treatment_country.value": dropdownCountry,
    }),
    [dropdownProcess, dropdownCountry]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default FinalTreatmentOwnTable;
